import {
  Box,
  Button,
  Card,
  DescriptionList,
  DescriptionListDetails,
  DescriptionListSet,
  DescriptionListTerm,
  Heading,
  Spinner,
} from "@twilio-paste/core";
import { ArrowBackIcon } from "@twilio-paste/icons/esm/ArrowBackIcon";
import { LogOutIcon } from "@twilio-paste/icons/esm/LogOutIcon";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LOGO_ALT, TWILIO_SENDGRID_LOGO } from "../../constants";
import ErrorAlert from "../../ErrorAlert";
import { SendGridSessionContext, useSendGridRouter, useSendGridSession } from "../../sendgrid-auth";
import { useUnifiedAccountsQuery, useUnifiedAuth } from "../../unified-auth";
import { bannerStatus } from "../../unified-auth/error";

export const UnifiedAccountSwitcher = () => {
  const { isAuthenticated: isAuthenticatedWithSendGrid } =
    useSendGridSession(SendGridSessionContext);
  // only set once based on the initial authentication state
  const [showLogoutAndBack] = useState(isAuthenticatedWithSendGrid);
  const { getHeldTokensOrSilentLogin, isLoadingWithIdP, fetchTokenAndRedirect, logout, error } =
    useUnifiedAuth();
  const [accessToken, setAccessToken] = useState<string>("");
  const [idpSessionError, setIdPSessionError] = useState<string>();
  const { data, isLoading, status } = useUnifiedAccountsQuery(accessToken);
  const reactRouterNavigate = useNavigate();
  const { navigate: navigateToMako } = useSendGridRouter();
  const [loginLoadingForUser, setLoginLoadingForUser] = useState<string>();

  useEffect(() => {
    // Wait until the IdP client has started up: until then it may still be filling the held tokens.
    if (isLoadingWithIdP) {
      return;
    }
    (async () => {
      // With no tokens held (e.g. after a reload), this starts a silent login whose callback
      // brings the user back here.
      const result = await getHeldTokensOrSilentLogin({ fromUnifiedAccountSwitching: true });
      if (result.status === "held") {
        setAccessToken(result.tokens.access_token);
      } else if (result.status === "unavailable") {
        setIdPSessionError(bannerStatus.IdPSessionError.banner);
      }
    })();
  }, [getHeldTokensOrSilentLogin, isLoadingWithIdP]);

  const loginForUser = useCallback(
    async (userId: string) => {
      await logout({ sendgridOnly: true, noRedirect: true });
      setLoginLoadingForUser(userId);
      fetchTokenAndRedirect(accessToken, undefined, { userId }).then(() =>
        setLoginLoadingForUser(undefined)
      );
    },
    [accessToken, fetchTokenAndRedirect, logout]
  );

  const accountsList = useMemo(
    () =>
      data?.results?.map(({ friendly_name, user_id }) => (
        <Card key={user_id} padding="space50" fillWidth>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <DescriptionList>
              <DescriptionListSet>
                <DescriptionListTerm>{friendly_name || "My SendGrid account"}</DescriptionListTerm>
                <DescriptionListDetails>{user_id}</DescriptionListDetails>
              </DescriptionListSet>
            </DescriptionList>
            <Button
              disabled={Boolean(loginLoadingForUser) && loginLoadingForUser !== user_id}
              loading={loginLoadingForUser === user_id}
              onClick={() => loginForUser(user_id)}
              // Fixed name: the user_id must not become the action name.
              data-faro-user-action-name="account-switcher-login"
              variant="secondary">
              Login
            </Button>
          </Box>
        </Card>
      )) ?? null,
    [data, loginForUser, loginLoadingForUser]
  );

  const showLoading = isLoading || status === "pending";
  // if there is an error fetching accounts this error will take precedence over unified auth errs
  // presumably it is not possible to proceed and get a unified auth error anyway
  const accountsError = data?.error && (
    <p style={{ margin: "0 10px" }}>
      We are unable to fetch your accounts at this time. Please try again later.
    </p>
  );

  return (
    <>
      {(accountsError || error || idpSessionError) && (
        <ErrorAlert input={accountsError || error || idpSessionError!} />
      )}
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        width="100vw"
        padding="space60">
        <Box display="flex" rowGap="space110" columnGap="space110" alignItems="flex-start">
          <Box whiteSpace="nowrap" paddingY="space30" paddingX="space40">
            {showLogoutAndBack && (
              <Button
                variant="link"
                data-faro-user-action-name="account-switcher-back"
                onClick={() => {
                  navigateToMako({
                    skipSecurityCheck: true,
                    mfaSettingIsVerified: false,
                    passwordResetRequired: false,
                    redirectToOAuth: false,
                    setup2FARequired: false,
                  });
                }}>
                <ArrowBackIcon decorative size="sizeIcon10" />
                Go Back
              </Button>
            )}
          </Box>
          <Box
            width="100%"
            display="flex"
            alignItems="center"
            flexDirection="column"
            rowGap="space160"
            columnGap="space110">
            <img width="250px" alt={LOGO_ALT} src={TWILIO_SENDGRID_LOGO} />
            <Box width="400px" />
            {showLoading && <Spinner decorative={false} title="Loading" size="sizeIcon80" />}
            {!showLoading && data?.results && (
              <>
                <Heading as="h1" variant="heading20" marginBottom="space0">
                  Select an account
                </Heading>

                <Box
                  display="flex"
                  rowGap="space40"
                  flexDirection="column"
                  alignItems="flex-start"
                  width="400px">
                  <Heading as="h2" variant="heading20" marginBottom="space0">
                    All Accounts
                  </Heading>
                  {accountsList}
                </Box>
              </>
            )}
          </Box>
          <Box whiteSpace="nowrap">
            {showLogoutAndBack && (
              <Button
                variant="secondary"
                data-faro-user-action-name="account-switcher-logout"
                onClick={() => reactRouterNavigate("/logout")}>
                Log Out <LogOutIcon decorative />
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};
